// =============================================================================
// AWANI - LUXURY ALPACA WOOL CLOTHING
// =============================================================================
// High-end custom clothing made from organic alpaca wool

import type { ContentComponent, NavItem } from '@/types';

// Navigation items
export const navItems: NavItem[] = [
  { label: 'Our Story', href: '/about' },
  { label: 'Collections', href: '/collections' },
];

// Footer links
export const footerLinks: NavItem[] = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Shipping & Returns', href: '/terms#shipping', external: true },
];

export const socialLinks: NavItem[] = [
  { label: 'Instagram', href: 'https://instagram.com/awani', external: true },
  { label: 'Email', href: '/about#contact-form', external: false },
];

// Homepage content components - Storybook hero with current hero as only page
export const homePageComponents: ContentComponent[] = [
  {
    id: 'storybook-hero-home',
    type: 'storybook-hero',
    title: '',
    content: '',
    fullWidth: true,
    metadata: {
      pages: [
        {
          id: 'hero-home',
          type: 'hero',
          title: 'Pure Alpaca. Zero Synthetics.',
          content: 'What you wear matters. Our garments leave nothing behind. Not on your skin, not in the soil. Biodegradable by design, crafted by hand in the Sacred Valley of Peru.',
          fullWidth: true,
          metadata: {
            image: '/images/hero2.mp4',
            overlayOpacity: 0.5,
            logo: '/images/logo.png',
            showLogo: true,
            minHeight: '90vh',
            textAlign: 'center'
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: 'products-ponchos-kimonos',
          type: 'products',
          title: 'Ponchos and Kimonos',
          content: 'Handcrafted ponchos and kimono pieces featuring rare shalina weavings. Each is entirely unique, showcasing traditional Peruvian weaving techniques passed down through generations. These are collector pieces.',
          metadata: {
            columns: 2,
            category: 'Ponchos and Kimonos'
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: 'products-pants-accessories',
          type: 'products',
          title: 'Pants and Accessories',
          content: 'Handcrafted alpaca wool pants in three styles. Unfortunately, these pants only come in approximately medium size (waist 28-31). Each pair is one-of-a-kind, crafted with the same reverence for natural materials.',
          metadata: {
            columns: 2,
            category: 'Pants and Accessories'
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: 'care-and-faq',
          type: 'two-column',
          title: '',
          content: '',
          metadata: {
            leftComponent: {
              id: 'care-instructions',
              type: 'text',
              title: 'Care Instructions',
              content: `
      <div style="background: rgb(97 64 10 / 10%); border: 2px solid rgba(239, 68, 68, 0.3); padding: 1rem 2rem; margin: 1rem 0; border-radius: 8px;">
        <h4 style="color: #8b5a3c; margin-bottom: 1rem;">⚠️ Critical: Hand Wash Only</h4>
        <p style="font-weight: 600; margin-bottom: 0.5rem;">IF YOU PUT THESE GARMENTS THROUGH A WASHING MACHINE...THEY WILL BE RUINED.</p>
        <p>These pieces require gentle, hand-wash care. They are living materials that respond to how you treat them.</p>
      </div>
      
      <h3>Washing Instructions</h3>
      <ul>
        <li><strong>Hand wash only.</strong> Fill a basin with cool to lukewarm water.</li>
        <li>Add a small amount of natural shampoo (no bleach, no softeners).</li>
        <li>Gently submerge garments. Swirl softly. Do not rub, stretch, or wring.</li>
        <li>Let soak for 3–5 minutes. Rinse gently in clean water.</li>
      </ul>
      
      <h3>Drying Instructions</h3>
      <ul>
        <li>Gently press excess water out (do not twist).</li>
        <li>Lay flat on a towel in shaded, ventilated space.</li>
        <li>Let air dry fully. Avoid direct sun.</li>
      </ul>
      
      <div class="content-highlight content-highlight--info" style="margin: 2rem 0; padding: 2rem; background: rgba(184, 160, 133, 0.1); border-left: 4px solid #8b7355;">
        <h4>Remember</h4>
        <p>Alpaca breathes with you. These garments rarely ask to be washed—just aired out, like old friends by a fire. Let them rest between wears, and they'll soften, reshape, and continue walking beside you.</p>
      </div>
      `,
              metadata: {
                textAlign: 'left',
                maxWidth: 900
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            },
            rightComponent: {
              id: 'faq',
              type: 'faq',
              title: 'Frequently Asked Questions',
              content: 'Common questions about Awani garments, care, and ordering.',
              metadata: {
                faqs: [
                  {
                    question: 'What makes Awani garments special?',
                    answer: 'Each piece is entirely one-of-a-kind, handcrafted by genuine Peruvian artisans in the Sacred Valley using pure alpaca wool. They are untreated, undyed, and biodegradable. Pure alpaca, zero synthetics.'
                  },
                  {
                    question: 'Why are the pieces one-of-a-kind?',
                    answer: 'Natural variations in the alpaca fiber, combined with traditional hand-weaving techniques and each artisan\'s individual touch, ensure that no two pieces are exactly alike. This is especially true for our kimono garments with rare shalina weavings.'
                  },
                  {
                    question: 'Can I wash these in a washing machine?',
                    answer: 'No. IF YOU PUT THESE GARMENTS THROUGH A WASHING MACHINE, THEY WILL BE RUINED. These pieces require gentle hand-washing only. Alpaca\'s natural properties mean they rarely need washing—just airing out between wears.'
                  },
                  {
                    question: 'What sizes are available?',
                    answer: 'Ponchos and kimono garments come in various sizes. Pants are available in approximately medium size (waist 28-31). Custom sizing available for made-to-order pieces.'
                  },
                  {
                    question: 'How long does delivery take?',
                    answer: 'Your custom piece will be delivered within 2-4 weeks of purchase. Each garment is made to order by artisans in the Sacred Valley.'
                  },
                  {
                    question: 'What is shalina weaving?',
                    answer: 'Shalina is a rare traditional Peruvian weaving technique. Our kimono garments featuring shalina weavings are collector pieces, each entirely unique and showcasing generations of weaving knowledge.'
                  },
                  {
                    question: 'Are new pieces available after the first batch?',
                    answer: 'Yes, new pieces will be made to order after the first batch. This ensures each garment is created with intention, connecting you directly to the artisans who craft it.'
                  },
                  {
                    question: 'What about the leather accessories?',
                    answer: 'There is a local Peruvian woman who makes beautiful hand-stitched leatherwork—holsters and bags. She has many more pieces available. Contact me and I\'ll share more pictures.'
                  },
                  {
                    question: 'Why are these garments biodegradable?',
                    answer: 'Because they are pure alpaca wool with zero synthetics, untreated and undyed, these garments will naturally return to the soil when their journey is complete—just as we do. This is part of honoring the land they came from.'
                  },
                  {
                    question: 'How do I care for my Awani piece?',
                    answer: 'As with all things wild and natural, alpaca responds best to gentle hands and slow care. Honor them, and they will soften with you over time. Hand wash only, air dry, and let them rest between wears. They\'ll continue walking beside you for years.'
                  }
                ]
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            }
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: 'story-and-contact',
          type: 'two-column',
          title: '',
          content: '',
          metadata: {
            leftComponent: {
              id: 'about-story',
              type: 'text',
              title: 'AWANI - Our Story',
              content: `
      <p>Awani is the Quechua word for "I Weave."</p>

      <p>Quechua is the indigenous language family that originated in central Peru—the land where our alpaca wool is born, where our artisans weave, and where each garment begins its journey.</p>

      <p>This is a co-op project. Alpaca textiles are sourced locally and crafted by Peruvian artisans in the Sacred Valley. Designs and styles are chosen with intention. Pieces are entirely one-of-a-kind, especially the kimono garments made with rare shalina weavings.</p>

      <div class="content-highlight content-highlight--info">
        <h4>The Intention</h4>
        <p>The intention of this project is to facilitate the sale of beautiful, natural clothing, made by Sacred Valley Peruvians. When you wear Awani, you're not just wearing a garment—you're wearing a connection to the land, to the hands that wove it, and to a way of being that honors both beauty and the earth.</p>
      </div>

      <p>This clothing is untreated, undyed, and biodegradable. When its journey is complete, it returns to the soil—just as we do. Treat it with the same reverence you'd give the land it came from.</p>
    `,
              metadata: {
                textAlign: 'left',
                maxWidth: 900,
                imageAlt: 'The Story of Awani',
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            },
            rightComponent: {
              id: 'contact-form',
              type: 'contact-form',
              title: 'Contact Us',
              content: 'Have questions about our collections or need assistance? Get in touch.',
              metadata: {},
              createdAt: new Date(),
              updatedAt: new Date(),
            }
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];
